import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  Checkbox,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Link,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import AddBoxIcon from "@mui/icons-material/AddBox";

const CategoryItem = ({
  item,
  disabled,
  onAddToMenu,
  defaultLanguage,
  categories,
  searchName,
  setFieldValue,
  selected,
  onToggleSelect,
  alreadyInMenu,
}) => {
  const { t, i18n } = useTranslation();
  const parentCategory = categories?.find(
    (category) => item?.parentCategory === category._id
  );
  return (
    <Card sx={{ mb: 1, ml: searchName ? 0 : item.level * 5, opacity: alreadyInMenu ? 0.5 : 1 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",

              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,

              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },

              "& svg": {
                fontSize: "1.25rem",
              },
            },
          }}
        >
          <Checkbox
            checked={!!selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={alreadyInMenu}
            size="small"
            sx={{ mr: 1, p: 0 }}
          />
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.label")}
            </Typography>
            <Typography variant={"body1"}>
              <Link
                component="button"
                variant="body1"
                onClick={(e) => {
                  e.preventDefault();
                  if (item?._id) {
                    // Open category detail in new tab to preserve menu context
                    window.open(
                      `/customer/dsok/categories/${item._id}`,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
                sx={{
                  cursor: item?._id ? "pointer" : "default",
                  textDecoration: "none",
                  color: "primary.main",
                  "&:hover": {
                    textDecoration: item?._id ? "underline" : "none",
                  },
                }}
              >
                {(item?.name && item?.name[defaultLanguage || i18n.language]) ||
                  ""}
              </Link>
              {item?._id && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1, fontSize: "0.75rem" }}
                >
                  (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={(e) => {
                      e.preventDefault();
                      if (item?._id) {
                        // Open category detail in new tab to preserve menu context
                        window.open(
                          `/customer/dsok/categories/${item._id}`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }}
                    sx={{
                      cursor: item?._id ? "pointer" : "default",
                      textDecoration: "none",
                      color: "text.secondary",
                      "&:hover": {
                        textDecoration: item?._id ? "underline" : "none",
                        color: "primary.main",
                      },
                    }}
                  >
                    {item._id}
                  </Link>
                  )
                </Typography>
              )}
            </Typography>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokCategories.parentCategory")}
            </Typography>
            <Typography variant={"body1"}>
              {(parentCategory?.name &&
                parentCategory?.name[defaultLanguage || i18n.language]) ||
                ""}
            </Typography>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokCategories.includeInMenu")}
            </Typography>
            <Typography variant={"body1"}>
              {`${item?.include_in_menu}` === "1"
                ? t("buttons.yes")
                : t("buttons.no")}
            </Typography>
          </Div>
          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.dsokCategories.menuType")}
            </Typography>
            <Typography variant={"body1"}>{item?.menuType}</Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 2 auto", md: 0 },
              px: 1,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{
                display: { xs: "none", md: "block" },
              }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flex: { direction: "row", flexWrap: "wrap" },
                minWidth: 70,
              }}
            >
              <Div>
                <IconButton
                  size="small"
                  variant={"contained"}
                  disabled={disabled || alreadyInMenu}
                  onClick={() => onAddToMenu(item, { setFieldValue })}
                >
                  <Tooltip title={t("buttons.addToMenu")} placement="top-start">
                    <AddBoxIcon />
                  </Tooltip>
                </IconButton>
              </Div>
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};
/* Todo item prop define */
export default CategoryItem;
